import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper';
import 'swiper/swiper-bundle.min.css';
import illustration from '../../assets/images/art_1.png';

import Art from './Art';

let youtubeApiLoader = null;

const loadYouTubeApi = () => {
    if (typeof window === 'undefined') return Promise.reject(new Error('Window is unavailable.'));
    if (window.YT && window.YT.Player) return Promise.resolve(window.YT);

    if (!youtubeApiLoader) {
        youtubeApiLoader = new Promise((resolve) => {
            const existingScript = document.querySelector('script[src="https://www.youtube.com/iframe_api"]');

            const previousReady = window.onYouTubeIframeAPIReady;
            window.onYouTubeIframeAPIReady = () => {
                if (typeof previousReady === 'function') previousReady();
                resolve(window.YT);
            };

            if (existingScript) return;

            const script = document.createElement('script');
            script.src = 'https://www.youtube.com/iframe_api';
            script.async = true;
            document.body.appendChild(script);
        });
    }

    return youtubeApiLoader;
};

const getYouTubeVideoId = (url) => {
    if (!url || typeof url !== 'string') return null;

    const trimmed = url.trim();
    if (!trimmed) return null;

    const shortMatch = trimmed.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
    if (shortMatch) return shortMatch[1];

    const watchMatch = trimmed.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
    if (watchMatch) return watchMatch[1];

    const embedMatch = trimmed.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/);
    if (embedMatch) return embedMatch[1];

    return null;
};

const YOUTUBE_QUALITY_PREFERENCE = [
    'highres',
    'hd2160',
    'hd1440',
    'hd1080',
    'hd720',
    'large',
    'medium',
    'small',
    'tiny',
];

const pickPreferredQuality = (levels = [], minimumQuality = 'hd1080') => {
    if (!Array.isArray(levels) || levels.length < 1) return minimumQuality;

    const available = levels.filter((level) => YOUTUBE_QUALITY_PREFERENCE.includes(level));
    if (available.length < 1) return minimumQuality;

    // Always prefer the highest quality available first.
    const highestAvailable = YOUTUBE_QUALITY_PREFERENCE.find((level) => available.includes(level));
    if (highestAvailable) return highestAvailable;

    return minimumQuality;
};

function ImageSlider(props) {
    const swiperRef = useRef(null);
    const tapTrackerRef = useRef({ index: null, time: 0 });
    const youtubeMountRefs = useRef(new Map());
    const youtubePlayersRef = useRef(new Map());
    const previousSwiperIndexRef = useRef(0);

    const [activeImageIndex, setActiveImageIndex] = useState(null);
    const [loadedImages, setLoadedImages] = useState({});
    const [brokenImages, setBrokenImages] = useState({});
    const [activeSwiperIndex, setActiveSwiperIndex] = useState(0);
    const [isTouchPlaybackMode, setIsTouchPlaybackMode] = useState(false);

    const youtubePlaybackQuality = props.youtubePlaybackQuality || 'hd1080';

    const youtubeVideoIds = useMemo(() => {
        const urls = Array.isArray(props.youtubeUrls)
            ? props.youtubeUrls
            : props.youtubeUrl
              ? [props.youtubeUrl]
              : [];

        return urls.map(getYouTubeVideoId).filter(Boolean);
    }, [props.youtubeUrl, props.youtubeUrls]);

    const slides = useMemo(() => {
        const imageSlides = props.images.map((imageSlide, imageIndex) => ({
            type: 'image',
            imageIndex,
            ...imageSlide,
        }));

        if (youtubeVideoIds.length < 1) return imageSlides;

        const youtubeSlides = youtubeVideoIds.map((videoId, videoOrder) => ({
            type: 'youtube',
            videoId,
            videoOrder,
        }));

        return [...imageSlides, ...youtubeSlides];
    }, [props.images, youtubeVideoIds]);

    const activeSlide = activeImageIndex == null ? null : props.images[activeImageIndex];
    const isActiveVideoSlide = slides[activeSwiperIndex]?.type === 'youtube';
    const isBeforeVideoSlide = slides[activeSwiperIndex + 1]?.type === 'youtube';

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;

        const mediaQuery = window.matchMedia('(max-width: 991px), (pointer: coarse)');
        const updateMode = (event) => setIsTouchPlaybackMode(!!event.matches);

        setIsTouchPlaybackMode(mediaQuery.matches);

        if (typeof mediaQuery.addEventListener === 'function') {
            mediaQuery.addEventListener('change', updateMode);
            return () => mediaQuery.removeEventListener('change', updateMode);
        }

        mediaQuery.addListener(updateMode);
        return () => mediaQuery.removeListener(updateMode);
    }, []);
    
    const resetAutoplayTimer = useCallback((swiperInstance) => {
        const swiper = swiperInstance || swiperRef.current?.swiper;
        if (!swiper || !swiper.autoplay) return;

        swiper.autoplay.stop();
        swiper.autoplay.start();
    }, []);

    const applyPreferredYouTubeQuality = useCallback(
        (player) => {
            if (!player || typeof player.setPlaybackQuality !== 'function') return;

            let targetQuality = youtubePlaybackQuality;
            const levels =
                typeof player.getAvailableQualityLevels === 'function'
                    ? player.getAvailableQualityLevels() || []
                    : [];

            targetQuality = pickPreferredQuality(levels, youtubePlaybackQuality);

            player.setPlaybackQuality(targetQuality);

            if (typeof player.setPlaybackQualityRange === 'function') {
                player.setPlaybackQualityRange(targetQuality);
            }
        },
        [youtubePlaybackQuality]
    );

    const enforcePreferredYouTubeQuality = useCallback(
        (slideIndex, delayList = [0, 400, 1200, 2400]) => {
            delayList.forEach((delay) => {
                window.setTimeout(() => {
                    const player = youtubePlayersRef.current.get(slideIndex);
                    if (!player) return;
                    applyPreferredYouTubeQuality(player);
                }, delay);
            });
        },
        [applyPreferredYouTubeQuality]
    );

    useEffect(() => {
        if (activeImageIndex == null) return undefined;

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setActiveImageIndex(null);
                return;
            }

            if (event.key === 'ArrowLeft') {
                setActiveImageIndex((currentIndex) => {
                    if (currentIndex == null) return currentIndex;
                    return currentIndex === 0 ? props.images.length - 1 : currentIndex - 1;
                });
            }

            if (event.key === 'ArrowRight') {
                setActiveImageIndex((currentIndex) => {
                    if (currentIndex == null) return currentIndex;
                    return currentIndex === props.images.length - 1 ? 0 : currentIndex + 1;
                });
            }
        };

        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            document.body.style.overflow = '';
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [activeImageIndex, props.images.length]);

    const goToPrevSlide = () => {
        if (swiperRef.current && swiperRef.current.swiper) {
            swiperRef.current.swiper.slidePrev();
            if (!isActiveVideoSlide) {
                resetAutoplayTimer(swiperRef.current.swiper);
            }
        }
    };

    const goToNextSlide = () => {
        if (swiperRef.current && swiperRef.current.swiper) {
            swiperRef.current.swiper.slideNext();
            if (!isActiveVideoSlide) {
                resetAutoplayTimer(swiperRef.current.swiper);
            }
        }
    };

    const openImageViewer = (index) => {
        setActiveImageIndex(index);
    };

    const closeImageViewer = () => {
        setActiveImageIndex(null);
    };

    const showNextImage = () => {
        setActiveImageIndex((currentIndex) => {
            if (currentIndex == null) return currentIndex;
            return currentIndex === props.images.length - 1 ? 0 : currentIndex + 1;
        });
    };

    const handleImageDoubleTap = (index) => {
        const now = Date.now();
        const lastTap = tapTrackerRef.current;
        const isDoubleTap = lastTap.index === index && now - lastTap.time < 320;

        if (isDoubleTap) {
            tapTrackerRef.current = { index: null, time: 0 };
            openImageViewer(index);
            return;
        }

        tapTrackerRef.current = { index, time: now };
    };

    const handleImageLoaded = (index) => {
        setLoadedImages((current) => ({ ...current, [index]: true }));
    };

    const handleImageError = (index) => {
        setBrokenImages((current) => ({ ...current, [index]: true }));
    };

    const pauseVideo = useCallback((slideIndex = activeSwiperIndex) => {
        const player = youtubePlayersRef.current.get(slideIndex);
        if (!player || typeof player.pauseVideo !== 'function') return;
        player.pauseVideo();
    }, [activeSwiperIndex]);

    const pauseOtherVideos = useCallback((activeIndex) => {
        youtubePlayersRef.current.forEach((player, index) => {
            if (index === activeIndex) return;
            if (!player || typeof player.pauseVideo !== 'function') return;
            player.pauseVideo();
        });
    }, []);

    useEffect(() => {
        if (youtubeVideoIds.length < 1) return undefined;

        let cancelled = false;

        loadYouTubeApi()
            .then((YT) => {
                if (cancelled) return;

                slides.forEach((slide, slideIndex) => {
                    if (slide.type !== 'youtube') return;
                    if (youtubePlayersRef.current.has(slideIndex)) return;

                    const mountNode = youtubeMountRefs.current.get(slideIndex);
                    if (!mountNode) return;

                    const player = new YT.Player(mountNode, {
                        videoId: slide.videoId,
                        playerVars: {
                            controls: 0,
                            modestbranding: 1,
                            rel: 0,
                            iv_load_policy: 3,
                            fs: 0,
                            disablekb: 1,
                            playsinline: 1,
                            cc_load_policy: 0,
                            autoplay: 0,
                        },
                        events: {
                            onReady: (event) => {
                                const readyPlayer = event?.target || player;
                                youtubePlayersRef.current.set(slideIndex, readyPlayer);
                                enforcePreferredYouTubeQuality(slideIndex);

                                if (
                                    !isTouchPlaybackMode &&
                                    activeSwiperIndex === slideIndex &&
                                    typeof readyPlayer.playVideo === 'function'
                                ) {
                                    readyPlayer.playVideo();
                                }
                            },
                            onStateChange: (event) => {
                                if (!event || typeof event.data !== 'number') return;

                                if (event.data === 1) {
                                    if (activeSwiperIndex === slideIndex) {
                                        pauseOtherVideos(slideIndex);
                                        enforcePreferredYouTubeQuality(slideIndex, [0, 300, 900, 1800]);
                                    }
                                    return;
                                }

                                if (event.data === 2) {
                                    return;
                                }

                                if (event.data === 0 && activeSwiperIndex === slideIndex) {
                                    const swiper = swiperRef.current?.swiper;
                                    if (swiper) {
                                        swiper.slideNext();
                                    }
                                }
                            },
                        },
                    });

                    youtubePlayersRef.current.set(slideIndex, player);
                });
            })
            .catch(() => {});

        return () => {
            cancelled = true;
        };
    }, [activeSwiperIndex, enforcePreferredYouTubeQuality, isTouchPlaybackMode, pauseOtherVideos, slides, youtubeVideoIds]);

    useEffect(() => {
        const videoIndexes = slides
            .map((slide, index) => (slide.type === 'youtube' ? index : -1))
            .filter((index) => index !== -1);

        youtubePlayersRef.current.forEach((player, index) => {
            if (!videoIndexes.includes(index)) {
                if (player && typeof player.destroy === 'function') {
                    player.destroy();
                }
                youtubePlayersRef.current.delete(index);
                youtubeMountRefs.current.delete(index);
            }
        });
    }, [slides]);

    useEffect(() => {
        if (youtubeVideoIds.length < 1) return;

        const previousIndex = previousSwiperIndexRef.current;
        const enteredVideoSlide = slides[previousIndex]?.type !== 'youtube' && isActiveVideoSlide;
        const switchedBetweenVideoSlides =
            slides[previousIndex]?.type === 'youtube' &&
            slides[activeSwiperIndex]?.type === 'youtube' &&
            previousIndex !== activeSwiperIndex;

        if (!isActiveVideoSlide) {
            if (slides[previousIndex]?.type === 'youtube') {
                pauseVideo(previousIndex);
            }
            previousSwiperIndexRef.current = activeSwiperIndex;
            return;
        }

        if (switchedBetweenVideoSlides) {
            pauseVideo(previousIndex);
        }

        const activePlayer = youtubePlayersRef.current.get(activeSwiperIndex);

        if (
            !isTouchPlaybackMode &&
            enteredVideoSlide &&
            activePlayer &&
            typeof activePlayer.playVideo === 'function'
        ) {
            activePlayer.playVideo();
        }

        pauseOtherVideos(activeSwiperIndex);

        previousSwiperIndexRef.current = activeSwiperIndex;
    }, [activeSwiperIndex, isActiveVideoSlide, isTouchPlaybackMode, pauseOtherVideos, pauseVideo, slides, youtubeVideoIds]);

    useEffect(() => {
        const swiper = swiperRef.current?.swiper;
        if (!swiper || youtubeVideoIds.length < 1) return;

        swiper.allowTouchMove = !isActiveVideoSlide;

        if (swiper.autoplay) {
            if (isActiveVideoSlide) {
                swiper.autoplay.stop();
            } else if (isBeforeVideoSlide) {
                // Prevent snapping right back into video after navigating away from it.
                swiper.autoplay.stop();
            } else {
                swiper.autoplay.start();
                resetAutoplayTimer(swiper);
            }
        }
    }, [isActiveVideoSlide, isBeforeVideoSlide, resetAutoplayTimer, youtubeVideoIds.length]);

    useEffect(() => {
        const players = youtubePlayersRef.current;
        const mounts = youtubeMountRefs.current;

        return () => {
            players.forEach((player) => {
                if (player && typeof player.destroy === 'function') {
                    player.destroy();
                }
            });
            players.clear();
            mounts.clear();
        };
    }, []);

    return (
        <>
            <div className="image-slider project-slider">
                <Swiper
                    ref={swiperRef}
                    className="testimonial-slider"
                    modules={[Autoplay, Pagination]}
                    slidesPerView={1}
                    grabCursor={true}
                    autoplay={{
                        delay: 5000,
                        disableOnInteraction: false,
                        pauseOnMouseEnter: true,
                    }}
                    loop={false}
                    speed={650}
                    followFinger={true}
                    threshold={8}
                    touchRatio={1.6}
                    longSwipes={true}
                    longSwipesRatio={0.33}
                    longSwipesMs={180}
                    shortSwipes={true}
                    resistance={true}
                    resistanceRatio={0.45}
                    watchSlidesProgress={true}
                    pagination={{
                        clickable: true,
                    }}
                    onSlideChange={(swiper) => {
                        setActiveSwiperIndex(swiper.activeIndex);
                        const nextIsVideo = slides[swiper.activeIndex]?.type === 'youtube';
                        const nextSlideIsVideo = slides[swiper.activeIndex + 1]?.type === 'youtube';
                        if (!nextIsVideo && !nextSlideIsVideo) {
                            resetAutoplayTimer(swiper);
                        }
                    }}
                >
                    {slides.map((slide, index) => {
                        if (slide.type === 'youtube') {
                            return (
                                <SwiperSlide key={`youtube-${slide.videoId}-${slide.videoOrder}`}>
                                    <div className="single-image image-slider-video-frame">
                                        <div
                                            ref={(node) => {
                                                if (node) {
                                                    youtubeMountRefs.current.set(index, node);
                                                } else {
                                                    youtubeMountRefs.current.delete(index);
                                                }
                                            }}
                                            className="image-slider-youtube-player"
                                        />
                                    </div>
                                </SwiperSlide>
                            );
                        }

                        const isLoaded = !!loadedImages[slide.imageIndex];
                        const isBroken = !!brokenImages[slide.imageIndex];

                        return (
                            <SwiperSlide key={index}>
                                <button
                                    type="button"
                                    className={`single-image image-slider-tap-target${
                                        isLoaded ? ' image-slider-tap-target-loaded' : ''
                                    }${isBroken ? ' image-slider-tap-target-missing' : ''}`}
                                    onTouchEnd={() => handleImageDoubleTap(slide.imageIndex)}
                                    onDoubleClick={() => openImageViewer(slide.imageIndex)}
                                    aria-label={`Open image ${slide.imageIndex + 1} of ${props.images.length}`}
                                >
                                    <span className="image-slider-loading-layer" aria-hidden="true" />
                                    <span className="image-slider-fallback-layer" aria-hidden="true">
                                        Image unavailable
                                    </span>
                                    <img
                                        src={slide.image}
                                        alt={slide.alt}
                                        loading={slide.imageIndex === 0 ? 'eager' : 'lazy'}
                                        onLoad={() => handleImageLoaded(slide.imageIndex)}
                                        onError={() => handleImageError(slide.imageIndex)}
                                    />
                                </button>
                            </SwiperSlide>
                        );
                    })}
                </Swiper>

                {props.swiperNav && (
                    <div className="slider--nav">
                        <div className="slider--nav-prev" onClick={goToPrevSlide}>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 43.75 43.564">
                                <title>arrow-left</title>
                                <path
                                    d="M22.612.343l.69.691h0a1.171,1.171,0,0,1,0,1.656L5.87,20.122H42.579a1.172,1.172,0,0,1,1.172,1.172v.976a1.172,1.172,0,0,1-1.172,1.172H5.87L23.3,40.873h0a1.171,1.171,0,0,1,0,1.656l-.69.692a1.174,1.174,0,0,1-1.658,0L.344,22.611h0a1.172,1.172,0,0,1,0-1.657L20.954.343A1.174,1.174,0,0,1,22.612.343Z"
                                    transform="translate(0 0)"
                                ></path>
                            </svg>
                        </div>
                        <div className="slider--nav-next" onClick={goToNextSlide}>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 43.75 43.564">
                                <title>arrow</title>
                                <path
                                    d="M1027.744,1417.179l20.61,20.61a1.172,1.172,0,0,1,0,1.658l-20.61,20.611a1.174,1.174,0,0,1-1.658,0l-.69-.692a1.171,1.171,0,0,1,0-1.657l17.431-17.431h-36.708a1.172,1.172,0,0,1-1.172-1.172v-.976a1.172,1.172,0,0,1,1.172-1.172h36.708l-17.431-17.431a1.171,1.171,0,0,1,0-1.657l.69-.691A1.174,1.174,0,0,1,1027.744,1417.179Z"
                                    transform="translate(-1004.947 -1416.836)"
                                ></path>
                            </svg>
                        </div>
                    </div>
                )}
            </div>

            {activeSlide &&
                createPortal(
                    <div
                        className="image-slider-lightbox"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Expanded gallery image"
                        onClick={closeImageViewer}
                    >
                        <div
                            className="image-slider-lightbox-content"
                            onClick={(event) => event.stopPropagation()}
                        >
                            <button
                                type="button"
                                className="image-slider-lightbox-image-button"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    showNextImage();
                                }}
                                aria-label={
                                    props.images.length > 1
                                        ? 'Show next image'
                                        : 'Expanded gallery image'
                                }
                            >
                                <img src={activeSlide.image} alt={activeSlide.alt} />
                            </button>
                        </div>
                    </div>,
                    document.body
                )}

            {!props.noArt && <Art art={illustration} type="3" />}
        </>
    );
}

export default ImageSlider;
